<script setup>
import { computed } from 'vue'

// CSS mock-up of a site's home page per template, for the template picker
// (New Site) and Settings › General. No assets — boxes in surface colours,
// like SiteDisplayThumb.vue for the Display cards.
const props = defineProps({
	/** `catalog` | `broadcast` */
	template: {
		type: String,
		required: true,
	},
	active: {
		type: Boolean,
		default: false,
	},
})

const accent = computed(() => (props.active ? 'bg-main' : 'bg-surface-6'))
const block = 'bg-surface-5'
</script>

<template>
	<div class="relative h-20 w-full overflow-hidden rounded-md bg-surface-2 ring-1 ring-surface-4" aria-hidden="true" :data-template="template">
		<!-- header bar shared by both -->
		<div class="absolute inset-x-2 top-1.5 flex h-1.5 items-center justify-between">
			<span class="h-1.5 w-6 rounded-sm bg-white/70" />
			<span class="flex gap-0.5"><span class="h-1.5 w-2 rounded-sm" :class="block" /><span class="h-1.5 w-2 rounded-sm" :class="block" /><span class="h-1.5 w-3 rounded-sm" :class="accent" /></span>
		</div>

		<!-- Broadcast: one big player with a chat column, service times + about underneath -->
		<template v-if="template === 'broadcast'">
			<div class="absolute left-2 right-2 flex gap-1" style="top: 1.125rem; height: 2rem">
				<div class="relative flex-1 rounded-sm bg-gradient-to-br from-surface-6 to-surface-4">
					<span class="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded-sm bg-black/40 px-0.5 py-px">
						<span class="size-1 rounded-full bg-error" /><span class="h-0.5 w-2 rounded-sm bg-white/80" />
					</span>
					<span class="absolute inset-0 m-auto size-2.5 rounded-full bg-white/80" />
				</div>
				<div class="flex w-5 flex-col gap-0.5 rounded-sm p-0.5" :class="block">
					<span v-for="i in 4" :key="i" class="h-0.5 rounded-sm bg-white/40" :class="i % 2 ? 'w-full' : 'w-2/3'" />
				</div>
			</div>
			<div class="absolute inset-x-2 bottom-1.5 grid grid-cols-[3fr_2fr] gap-1" style="height: 1.5rem">
				<div class="flex flex-col gap-0.5 pt-0.5">
					<span class="h-0.5 w-8 rounded-sm bg-white/60" />
					<span class="h-0.5 w-full rounded-sm bg-white/25" />
					<span class="h-0.5 w-5/6 rounded-sm bg-white/25" />
					<span class="h-0.5 w-2/3 rounded-sm bg-white/25" />
				</div>
				<div class="flex flex-col gap-0.5 rounded-sm p-1" :class="block">
					<span class="h-0.5 w-3/4 rounded-sm bg-white/60" />
					<span class="h-0.5 w-full rounded-sm bg-white/30" />
					<span class="mt-auto h-1 w-4 self-end rounded-sm" :class="accent" />
				</div>
			</div>
		</template>

		<!-- Catalog: featured hero, category pills and a grid of cards -->
		<template v-else>
			<div class="absolute inset-x-2 rounded-sm bg-gradient-to-r from-surface-6 to-surface-4" style="top: 1.125rem; height: 1.5rem">
				<span class="absolute bottom-1 left-1 h-1 w-7 rounded-sm bg-white/70" />
				<span class="absolute bottom-1 left-9 h-1 w-3 rounded-sm" :class="accent" />
			</div>
			<div class="absolute left-2 flex gap-0.5" style="top: 2.875rem">
				<span class="h-1.5 w-4 rounded-full" :class="accent" />
				<span v-for="i in 3" :key="i" class="h-1.5 w-3 rounded-full" :class="block" />
			</div>
			<div class="absolute inset-x-2 bottom-1.5 grid grid-cols-5 gap-1" style="height: 0.875rem">
				<span v-for="i in 5" :key="i" class="rounded-sm" :class="block" />
			</div>
		</template>
	</div>
</template>
